import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import API_URL from '../../constants/environment';
import SessionContext from '../../contexts/SessionContext';
import { trackExtractionObservationJob } from './extractionObservationJobTracker';

export const EXTRACTION_LAYOUT_FIELDS = ['roNumber', 'invoiceNumber', 'vendor', 'invoiceDate'] as const;
export type ExtractionLayoutField =
    | (typeof EXTRACTION_LAYOUT_FIELDS)[number]
    | 'purchaseOrderNumber'
    | 'shopRoNumber'
    | 'taxesTotal'
    | 'orderTotal';

const ALL_EXTRACTION_LAYOUT_FIELDS: ExtractionLayoutField[] = [
    'roNumber',
    'purchaseOrderNumber',
    'invoiceNumber',
    'shopRoNumber',
    'invoiceDate',
];

/** PO/NEX header is a distinct printed field on return slips and in org `both` identifier mode. */
export function shouldTeachPurchaseOrderLayout(args: {
    invoiceType?: string | null;
    nexReconciliationIdentifierMode?: string | null;
}): boolean {
    return args.invoiceType === 'return_slip' || args.nexReconciliationIdentifierMode === 'both';
}

export function layoutFieldsForTeaching(includePurchaseOrder: boolean): ExtractionLayoutField[] {
    if (!includePurchaseOrder) {
        return ['roNumber', 'invoiceNumber', 'shopRoNumber', 'invoiceDate'];
    }
    return ALL_EXTRACTION_LAYOUT_FIELDS;
}

export interface ExtractionObservation {
    id: string;
    field: ExtractionLayoutField | 'shopRoNumber';
    extracted: string;
    corrected: string;
    enhancedHint: string;
    nearbyTokens: string[];
    invoiceUuid: string;
    at: string;
}

export interface ExtractionObservationsResponse {
    observations: ExtractionObservation[];
    invoiceType: string | null;
    vendorKey: string | null;
    canEdit: boolean;
}

const getInvoiceExtractionObservations = async (
    invoiceId: string,
    sessionId: string | null,
): Promise<ExtractionObservationsResponse> => {
    const { data } = await axios.post(
        `${API_URL}/invoice/extraction-layout`,
        { invoiceId },
        { headers: { Authorization: sessionId } },
    );
    return data;
};

export type RememberExtractionObservationResponse =
    | { ok: true; observation: ExtractionObservation; compiledInjection: string }
    | { status: 'processing'; jobId: string; invoiceId: string };

export const rememberInvoiceExtractionObservation = async (
    input: { invoiceId: string; field: ExtractionLayoutField | 'shopRoNumber'; corrected: string },
    sessionId: string | null,
): Promise<RememberExtractionObservationResponse> => {
    const { data } = await axios.post(
        `${API_URL}/invoice/extraction-observation/remember`,
        input,
        { headers: { Authorization: sessionId }, timeout: 120_000 },
    );
    return data;
};

export const updateInvoiceExtractionObservation = async (
    input: { invoiceId: string; observationId: string; enhancedHint: string },
    sessionId: string | null,
): Promise<{ ok: boolean; observation: ExtractionObservation; compiledInjection: string }> => {
    const { data } = await axios.post(
        `${API_URL}/invoice/extraction-observation/update`,
        input,
        { headers: { Authorization: sessionId }, timeout: 120_000 },
    );
    return data;
};

export const deleteInvoiceExtractionObservation = async (
    input: { invoiceId: string; observationId: string },
    sessionId: string | null,
): Promise<{ ok: boolean; compiledInjection: string }> => {
    const { data } = await axios.post(
        `${API_URL}/invoice/extraction-observation/update`,
        { ...input, delete: true },
        { headers: { Authorization: sessionId }, timeout: 120_000 },
    );
    return data;
};

export const useGetInvoiceExtractionLayout = (invoiceId: string, enabled: boolean) => {
    const sessionContext = useContext(SessionContext);
    return useQuery({
        queryKey: ['/invoice/extraction-layout', invoiceId],
        queryFn: () => getInvoiceExtractionObservations(invoiceId, sessionContext?.sessionId ?? null),
        enabled: enabled && Boolean(invoiceId),
    });
};

export const useRememberInvoiceExtractionObservation = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (input: { invoiceId: string; field: ExtractionLayoutField | 'shopRoNumber'; corrected: string }) => {
            const data = await rememberInvoiceExtractionObservation(input, sessionContext?.sessionId ?? null);
            if ('status' in data && data.status === 'processing' && data.jobId) {
                trackExtractionObservationJob({
                    jobId: data.jobId,
                    invoiceId: data.invoiceId,
                });
            }
            return data;
        },
        onSuccess: (data, variables) => {
            if ('status' in data && data.status === 'processing') return;
            void queryClient.invalidateQueries({ queryKey: ['/invoice/extraction-layout', variables.invoiceId] });
        },
    });
};

export const useUpdateInvoiceExtractionObservation = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: { invoiceId: string; observationId: string; enhancedHint: string }) =>
            updateInvoiceExtractionObservation(input, sessionContext?.sessionId ?? null),
        onSuccess: (_data, variables) => {
            void queryClient.invalidateQueries({ queryKey: ['/invoice/extraction-layout', variables.invoiceId] });
        },
    });
};

export const useDeleteInvoiceExtractionObservation = () => {
    const sessionContext = useContext(SessionContext);
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (input: { invoiceId: string; observationId: string }) =>
            deleteInvoiceExtractionObservation(input, sessionContext?.sessionId ?? null),
        onSuccess: (_data, variables) => {
            void queryClient.invalidateQueries({ queryKey: ['/invoice/extraction-layout', variables.invoiceId] });
        },
    });
};
